import { CreditCard } from 'lucide-react';

import type { MccSuggestion, MerchantCategoryCodeSummary } from '@ballerine/common';

import { ctw } from '@/common/utils/ctw';
import { CopyButton } from '@/components';
import { Separator } from '@/components/atoms/Separator';
import { ReportWrapper } from '../../report-shared/components';

type MerchantCategoryCodesSectionProps = {
  mccSummary: MerchantCategoryCodeSummary | null | undefined;
};

const formatMccDisplay = (code?: string | null, description?: string | null) =>
  [code, description].filter(Boolean).join(' - ');

export const MerchantCategoryCodesSection = ({ mccSummary }: MerchantCategoryCodesSectionProps) => {
  if (!mccSummary?.primary?.mcc) {
    return null;
  }

  const { primary, alternatives } = mccSummary;

  return (
    <ReportWrapper
      title="Merchant Category Codes"
      Icon={CreditCard}
      tooltip="Primary MCC assignment with supporting reasoning and alternative options."
      showTooltip={false}
    >
      <p className="-mt-3">Based on Visa Merchant Data Standards Manual</p>
      <div className={ctw('grid grid-cols-1', alternatives?.length && 'grid-cols-2 gap-6')}>
        <div>
          <p className="mb-4 mt-[1.35rem] inline-block rounded-sm bg-blue-600/20 px-2 py-0.5 text-sm font-medium uppercase tracking-tight text-blue-600">
            Primary
          </p>
          <MCCItem {...primary} />
        </div>

        {alternatives?.length ? (
          <div className="space-y-4 rounded-lg border p-5">
            <div className="inline-block rounded-sm bg-slate-400/20 px-2 py-0.5 text-sm font-medium uppercase tracking-tight text-slate-400">
              Alternatives
            </div>
            <div className="space-y-4">
              {alternatives.map((alternative, i) => (
                <>
                  <MCCItem
                    key={formatMccDisplay(alternative.mcc, alternative.description)}
                    {...alternative}
                  />
                  {i < alternatives.length - 1 && <Separator />}
                </>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    </ReportWrapper>
  );
};

const MCCItem = ({ mcc, description, reasoning }: MccSuggestion) => {
  return (
    <div>
      <p className="text-xl font-semibold text-slate-900">{mcc}</p>

      {description && (
        <div className="space-x-1 text-justify text-sm leading-relaxed">
          <span className="text-sm font-medium text-slate-900">{description}</span>
          <CopyButton className="inline" textToCopy={formatMccDisplay(mcc, description)} />
        </div>
      )}

      {reasoning && (
        <p className="whitespace-pre-line text-justify text-sm leading-relaxed text-neutral-700">
          {reasoning}
        </p>
      )}
    </div>
  );
};
